import type { ScopeRow } from 'src/features/workspaces/components/agents/scopeModel'

import type {
  McpConnection,
  McpConnectionFilters,
  McpMemberService,
  McpServiceId,
  McpServicePolicy
} from './types'

export const MCP_SERVICES: readonly McpServiceId[] = [
  'core',
  'crm',
  'notification',
  'squad'
]

/** Services whose MCP server is tenanted by an installed app instance. */
const INSTANCE_SERVICES: ReadonlySet<McpServiceId> = new Set(['crm', 'squad'])

export const PAT_MAX_DAYS = 365

export const isMcpServiceId = (value: unknown): value is McpServiceId =>
  typeof value === 'string' &&
  (MCP_SERVICES as readonly string[]).includes(value)

const byServiceOrder = <T extends { service: McpServiceId }>(a: T, b: T) =>
  MCP_SERVICES.indexOf(a.service) - MCP_SERVICES.indexOf(b.service)

export const sortByService = <T extends { service: McpServiceId }>(
  rows: T[]
): T[] => [...rows].sort(byServiceOrder)

/** What the Connect wizard offers. Core already drops services the member cannot access. */
export const connectableServices = (
  services: McpMemberService[]
): McpMemberService[] =>
  sortByService(services.filter((s) => s.is_enabled && !!s.mcp_url))

/** Admin views follow Core's own admin check, carried as `can_manage` (A8). */
export const canManageMcp = (
  services: Pick<McpMemberService, 'can_manage'>[]
) => services.some((s) => s.can_manage)

export const requiresInstance = (service: McpServiceId) =>
  INSTANCE_SERVICES.has(service)

/** Core's verdict: configured in this environment and, for CRM/Squad, installed. */
export const canEnablePolicy = (policy: Pick<McpServicePolicy, 'available'>) =>
  policy.available

export type EnableBlockedReason = 'notInstalled' | 'notConfigured'

/** Which hint explains a service Core reports as unavailable. */
export const enableBlockedReason = (
  policy: Pick<McpServicePolicy, 'service' | 'application' | 'available'>
): EnableBlockedReason | null => {
  if (policy.available) return null

  return requiresInstance(policy.service) && policy.application === null
    ? 'notInstalled'
    : 'notConfigured'
}

/** Catalog rows a connection of this service may carry. */
export const serviceScopeRows = (catalog: ScopeRow[], service: McpServiceId) =>
  catalog.filter((row) => row.app === service && !row.deprecated)

/** What a connection may carry: the picked codes the picker actually shows (`serviceScopeRows`). */
export const grantableScopes = (
  selected: Set<string>,
  rows: Pick<ScopeRow, 'code'>[]
): Set<string> =>
  new Set(rows.map((row) => row.code).filter((code) => selected.has(code)))

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate())

/** A PAT expires on a future day, at most `PAT_MAX_DAYS` days away. */
export const isPatExpiryAllowed = (candidate: Date, now: Date) => {
  const day = startOfDay(candidate).getTime()
  const today = startOfDay(now)
  const last = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() + PAT_MAX_DAYS
  ).getTime()

  return day > today.getTime() && day <= last
}

const DAY_MS = 24 * 60 * 60 * 1000

/** Whole days from now to the picked day, so "tomorrow" is 24 h and day 365 is the cap. */
export const patExpiryInstant = (day: Date, now: Date): Date => {
  const days = Math.round(
    (startOfDay(day).getTime() - startOfDay(now).getTime()) / DAY_MS
  )

  return new Date(now.getTime() + days * DAY_MS)
}

/** Same literals as Core's revoke reasons; anything else gets no label. */
export const MCP_REVOKE_REASONS = [
  'user',
  'admin',
  'policy_disabled',
  'personal_tokens_disabled',
  'client',
  'refresh_reuse',
  'membership_removed',
  'membership_blocked',
  'app_access_removed'
] as const

export type McpRevokeReason = (typeof MCP_REVOKE_REASONS)[number]

export const revokeReason = (value: unknown): McpRevokeReason | null =>
  (MCP_REVOKE_REASONS as readonly unknown[]).includes(value)
    ? (value as McpRevokeReason)
    : null

export const parseConnectionFilters = (
  params: Record<string, unknown>
): McpConnectionFilters => {
  const filters: McpConnectionFilters = {}
  if (isMcpServiceId(params.service)) filters.service = params.service
  const user = Number(params.user)
  if (Number.isInteger(user) && user > 0) filters.user = user

  return filters
}

export type ServiceOption = {
  service: McpServiceId
  selectable: boolean
  blocked: EnableBlockedReason | null
  /** Present for admins, who switch the service on or off right in the list. */
  policy?: McpServicePolicy
}

/** The wizard's service list: admins see every service, members only the ones they can connect. */
export const serviceOptions = (
  services: McpMemberService[],
  policies: McpServicePolicy[] | null
): ServiceOption[] => {
  const connectable = new Set(
    connectableServices(services).map((s) => s.service)
  )
  if (!policies) {
    return [...connectable].map((service) => ({
      service,
      selectable: true,
      blocked: null
    }))
  }

  return sortByService(policies).map((policy) => ({
    service: policy.service,
    selectable: policy.is_enabled && connectable.has(policy.service),
    blocked: policy.is_enabled ? null : enableBlockedReason(policy),
    policy
  }))
}

/** The OAuth connection a client made while the wizard waited: one that was not there before. */
export const newOauthConnection = (
  connections: McpConnection[],
  knownIds: ReadonlySet<string>,
  service: McpServiceId
): McpConnection | null =>
  connections.find(
    (c) =>
      c.service === service &&
      c.kind === 'oauth' &&
      c.status === 'active' &&
      !knownIds.has(c.id)
  ) ?? null
